import React, { useEffect, useRef } from 'react';
import { env } from '@/app/config/environment';
import type { Location } from '@/features/clubs/types/types';

interface GoogleMapsProps {
  location: Location;
  className?: string;
}

const GoogleMaps: React.FC<GoogleMapsProps> = ({ location, className = '' }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || !env.GOOGLE_MAPS_API_KEY || !location.latitude || !location.longitude) {
      return;
    }

    // Load Google Maps script if not already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${env.GOOGLE_MAPS_API_KEY}&libraries=places,marker&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Add a small delay to ensure all APIs are loaded
        setTimeout(initializeMap, 100);
      };
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
      };
      document.head.appendChild(script);
    } else {
      // Add a small delay to ensure APIs are ready
      setTimeout(initializeMap, 100);
    }

    function initializeMap(retryCount = 0) {
      if (!mapRef.current || !window.google || !window.google.maps || !window.google.maps.Map) {
        if (retryCount < 5) {
          console.warn(`Google Maps API not fully loaded yet, retrying... (${retryCount + 1}/5)`);
          setTimeout(() => initializeMap(retryCount + 1), 200);
          return;
        } else {
          console.error('Failed to load Google Maps API after multiple retries');
          return;
        }
      }

      const center = {
        lat: location.latitude!,
        lng: location.longitude!
      };

      // Map configuration - styles are controlled via Map ID in Cloud Console
      // To customize styles: Google Cloud Console → Maps Platform → Map Management → [Your Map ID] → Styling
      const mapConfig = {
        center,
        zoom: 15,
        mapId: 'DEMO_MAP_ID', // Required for Advanced Markers
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy',
        clickableIcons: true
      };

      mapInstanceRef.current = new google.maps.Map(mapRef.current, mapConfig);

      // Add marker using AdvancedMarkerElement
      new google.maps.marker.AdvancedMarkerElement({
        position: center,
        map: mapInstanceRef.current,
        title: `${location.address_line1}, ${location.city}, ${location.country}`
      });
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    };
  }, [location]);

  if (!env.GOOGLE_MAPS_API_KEY || !location.latitude || !location.longitude) {
    return (
      <div className={`bg-gray-800 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center p-4">
          <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-gray-400 text-sm">Map unavailable</p>
        </div>
      </div>
    );
  }

  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;

  return (
    <div className={`relative ${className}`}>
      <div ref={mapRef} className="w-full h-full overflow-hidden" />
      
      <a
        href={gmapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-4 right-15 bg-white/10 backdrop-blur-sm text-white py-2 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 shadow-lg hover:bg-white/20 transition-all duration-300 border border-white/20"
        aria-label="Open in Google Maps"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        Maps
      </a>
    </div>
  );
};

export default GoogleMaps;
